import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/prompts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { extractErrorMessage } from "@/lib/api-errors";
import { ROLES, ROLE_LABELS, type Role } from "@/lib/roles";
import type { PromptView } from "@/types/api";

function paramsLine(params: Record<string, unknown>): string {
  const model = params.model ? String(params.model) : "—";
  const maxTokens = params.max_tokens ? `, max_tokens ${params.max_tokens}` : "";
  return `${model}${maxTokens}`;
}

export function PromptsPage(): React.ReactElement {
  const { data: prompts = [], isLoading } = useQuery({
    queryKey: ["prompts"],
    queryFn: api.listPrompts,
  });

  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (selectedId === null && prompts.length) setSelectedId(prompts[0]?.id ?? null);
    // If the selected prompt disappeared, fall back to the first one.
    if (selectedId !== null && !prompts.some((p) => p.id === selectedId)) {
      setSelectedId(prompts[0]?.id ?? null);
    }
  }, [prompts, selectedId]);

  const current = prompts.find((p) => p.id === selectedId) ?? null;

  const grouped = ROLES.map((role) => ({
    role,
    items: prompts.filter((p) => p.role === role),
  }));

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Промпты</h1>
        <p className="text-sm text-muted-foreground">
          Библиотека промптов пайплайна ответов на отзывы. По каждой роли может быть
          несколько промптов; бренды ссылаются на конкретный промпт. Сохранение создаёт
          новую версию (старые остаются); генерация использует активную.
        </p>
      </div>

      <CreatePromptForm />

      {isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>}
      {!isLoading && prompts.length === 0 && (
        <p className="text-sm text-muted-foreground">Промптов ещё нет.</p>
      )}

      {grouped.map(
        (g) =>
          g.items.length > 0 && (
            <div key={g.role} className="space-y-2">
              <h2 className="text-sm font-semibold uppercase text-muted-foreground">
                {ROLE_LABELS[g.role] ?? g.role}
              </h2>
              <div className="flex flex-wrap gap-2">
                {g.items.map((p) => (
                  <Button
                    key={p.id}
                    variant={p.id === selectedId ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedId(p.id)}
                  >
                    {p.title}
                    <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                      v{p.active_version_no ?? "—"}
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          ),
      )}

      {current && <PromptEditor key={current.id} prompt={current} />}
    </div>
  );
}

function CreatePromptForm(): React.ReactElement {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<Role>("generator");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const reset = () => {
    setTitle("");
    setBody("");
    setRole("generator");
    setOpen(false);
  };

  const createMut = useMutation({
    mutationFn: () => api.createPrompt({ role, title, body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prompts"] });
      reset();
      toast.success("Промпт создан");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка создания")),
  });

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)}>Создать промпт</Button>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Новый промпт</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            createMut.mutate();
          }}
        >
          <div className="space-y-2">
            <Label>Роль</Label>
            <div className="flex gap-2">
              {ROLES.map((r) => (
                <Button
                  key={r}
                  type="button"
                  size="sm"
                  variant={role === r ? "default" : "outline"}
                  onClick={() => setRole(r)}
                >
                  {ROLE_LABELS[r]}
                </Button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-prompt-title">Название</Label>
            <Input
              id="new-prompt-title"
              placeholder="например: Генератор — премиум-бренды"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-prompt-body">Текст промпта</Label>
            <Textarea
              id="new-prompt-body"
              className="min-h-[240px] font-mono text-xs"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={createMut.isPending || !title.trim() || !body.trim()}
            >
              Создать
            </Button>
            <Button type="button" variant="ghost" onClick={reset}>
              Отмена
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function PromptEditor({ prompt }: { prompt: PromptView }): React.ReactElement {
  const qc = useQueryClient();
  const [loadedVersionNo, setLoadedVersionNo] = useState<number | null>(null);
  const [body, setBody] = useState("");
  const [changelog, setChangelog] = useState("");

  const versionToLoad = loadedVersionNo ?? prompt.active_version_no ?? null;

  const { data: versionDetail } = useQuery({
    queryKey: ["prompt-version", prompt.id, versionToLoad],
    queryFn: () => api.getPromptVersion(prompt.id, versionToLoad as number),
    enabled: versionToLoad !== null,
  });

  useEffect(() => {
    if (versionDetail) {
      setBody(versionDetail.body);
      setChangelog("");
    }
  }, [versionDetail]);

  const createMut = useMutation({
    mutationFn: () =>
      api.createPromptVersion(prompt.id, { body, changelog, activate: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prompts"] });
      setLoadedVersionNo(null);
      toast.success("Новая версия сохранена и активирована");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка сохранения")),
  });

  const activateMut = useMutation({
    mutationFn: (versionNo: number) =>
      api.activatePromptVersion(prompt.id, versionNo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prompts"] });
      setLoadedVersionNo(null);
      toast.success("Версия активирована");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка активации")),
  });

  const viewingOld =
    loadedVersionNo !== null && loadedVersionNo !== prompt.active_version_no;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {prompt.title}
            <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
              {ROLE_LABELS[prompt.role] ?? prompt.role}
            </span>
            {viewingOld && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                (просмотр v{loadedVersionNo}, активна v{prompt.active_version_no})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="body">Текст промпта</Label>
            <Textarea
              id="body"
              className="min-h-[320px] font-mono text-xs"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="changelog">Что изменилось (changelog)</Label>
            <Input
              id="changelog"
              placeholder="например: смягчил тон на негативе"
              value={changelog}
              onChange={(e) => setChangelog(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => createMut.mutate()}
              disabled={createMut.isPending || !body.trim()}
            >
              Сохранить как новую версию
            </Button>
            {viewingOld && (
              <Button variant="ghost" onClick={() => setLoadedVersionNo(null)}>
                К активной версии
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        <h2 className="text-lg font-semibold">История версий</h2>
        <ul className="space-y-2">
          {prompt.versions.map((v) => (
            <li
              key={v.id}
              className="flex flex-col gap-1 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-medium">
                  v{v.version_no}
                  {v.is_active && (
                    <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800 dark:bg-green-950 dark:text-green-300">
                      активна
                    </span>
                  )}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {paramsLine(v.params)}
                  {v.changelog ? ` · ${v.changelog}` : ""}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setLoadedVersionNo(v.version_no)}
                >
                  Показать
                </Button>
                {!v.is_active && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => activateMut.mutate(v.version_no)}
                    disabled={activateMut.isPending}
                  >
                    <Check className="size-4" /> Активировать
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
